import React from 'react';
import { Control, FieldErrors, UseFormRegister, useController, useFormContext, useWatch, RegisterOptions, FieldValues } from 'react-hook-form';
import { useProjectReadOnly } from '../../features/admin/projects/projectForm.helpers';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
const HandsontableModule = require('../../../node_modules/handsontable/dist/handsontable.full.min.js');
require('../../../node_modules/handsontable/dist/handsontable.full.min.css');

type AnyForm = Record<string, any>;

export type CellStyle = { bold?: boolean };
export type TableModel = {
  columns: string[];
  rows: string[][];
  cellStyles: Record<string, CellStyle>;
  columnWidths?: number[];
};
type HotInstance = any;

export function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="fieldGrid">{children}</div>;
}

export function TextField<TForm extends FieldValues>(props: {
  label:string;
  name: string;
  register: UseFormRegister<TForm>;
  rules?: RegisterOptions<TForm, any>;
  errors?: FieldErrors<TForm>;
  placeholder?: string;
  error?: any;
}) {
  const readOnly = useProjectReadOnly();
  return (
      <label className="label">
        {props.label}
        <input
            className="input"
            placeholder={props.placeholder}
            disabled={readOnly}
            {...props.register(props.name as any, props.rules)}
        />
        {props.error && <span className="error-message">{props.error.message}</span>}
      </label>
  );
}

export function SelectField<TForm extends AnyForm>(props: {
  label: string;
  name: string;
  register: UseFormRegister<TForm>;
  options: { value: string; label: string }[];
  errors?: FieldErrors<TForm>;
}) {
  const { t } = useTranslation();
  const readOnly = useProjectReadOnly();
  return (
      <label className="label">
        {props.label}
        <select
            className="input"
            disabled={readOnly}
            {...props.register(props.name as any)}
        >
          <option value="">{t('fields.selectOption')}</option>
          {props.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
          ))}
        </select>
      </label>
  );
}

export function TextAreaField<TForm extends AnyForm>(props: {
  label: string;
  name: string;
  register: UseFormRegister<TForm>;
  rows?: number;
  placeholder?: string;
}) {
  const readOnly = useProjectReadOnly();
  return (
      <label className="label">
        {props.label}
        <textarea
            className="textarea"
            rows={props.rows ?? 3}
            placeholder={props.placeholder}
            disabled={readOnly}
            {...props.register(props.name as any)}
        />
      </label>
  );
}

export function NumberField<TForm extends AnyForm>(props: {
  label: string;
  name: string;
  register: UseFormRegister<TForm>;
  step?: number;
}) {
  const readOnly = useProjectReadOnly();
  return (
      <label className="label">
        {props.label}
        <input
            className="input"
            type="number"
            step={props.step ?? 1}
            disabled={readOnly}
            {...props.register(props.name as any, {
              valueAsNumber: true,
              setValueAs: (v: unknown) => (v === '' || v === null || v === undefined ? 0 : Number(v)),
            })}
        />
      </label>
  );
}

export function DateField<TForm extends AnyForm>(props: {
  label: string;
  name: string;
  register: UseFormRegister<TForm>;
}) {
  const readOnly = useProjectReadOnly();
  return (
      <label className="label">
        {props.label}
        <input
            className="input"
            type="date"
            disabled={readOnly}
            {...props.register(props.name as any)}
        />
      </label>
  );
}

export function CheckboxField<TForm extends AnyForm>(props: { label: string; name: string; register: UseFormRegister<TForm> }) {
  const readOnly = useProjectReadOnly();
  if (readOnly && props.name.endsWith('publico')) {
    return null;
  }

  return (
      <label className="check">
        <input
            type="checkbox"
            disabled={readOnly}
            {...props.register(props.name as any)}
        />
        <span>{props.label}</span>
      </label>
  );
}

export function ToggleField<TForm extends AnyForm>(props: { label: string; name: string; register: UseFormRegister<TForm> }) {
  const { t } = useTranslation();
  const readOnly = useProjectReadOnly();
  const { control } = useFormContext<TForm>();
  const isChecked = useWatch({ control, name: props.name as any });

  const title = t('projectFormHelpers.bulkToggleTitle');
  
  const displayLabel = isChecked ? t('projectFormHelpers.public') : t('projectFormHelpers.private');

  return (
      <label
          style={{ display: 'flex', alignItems: 'center', cursor: readOnly ? 'not-allowed' : 'pointer', gap: '8px' }}
          onClick={(e) => e.stopPropagation()}
          title={title}
      >
        <span style={{ fontWeight: 500 }}>{displayLabel}</span>
        <input
            type="checkbox"
            disabled={readOnly}
            {...props.register(props.name as any)}
            style={{
              
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              width: '40px',
              height: '20px',
              backgroundColor: '#ef4444', 
              borderRadius: '10px',
              position: 'relative',
              cursor: readOnly ? 'not-allowed' : 'pointer',
              outline: 'none',
              transition: 'background-color 0.2s',
            }}
        />
        <style>{`
          input[type="checkbox"] {
            --toggle-thumb-left: 2px;
          }
          input[type="checkbox"]::before {
            content: '';
            position: absolute;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: white;
            top: 2px;
            left: var(--toggle-thumb-left);
            transition: left 0.2s, background-color 0.2s;
          }
          input[type="checkbox"]:checked {
            background-color: #22c55e !important; 
          }
          input[type="checkbox"]:checked::before {
            left: 22px; 
          }
        `}</style>
      </label>
  );
}


export function JsonField<TForm extends AnyForm>(props: {
  label: string;
  name: string;
  control: Control<TForm>;
  helper?: string;
}) {
  const { t } = useTranslation();
  const readOnly = useProjectReadOnly();
  const { field } = useController({ control: props.control, name: props.name as any });

  const value =
      field.value === null || field.value === undefined
          ? ''
          : typeof field.value === 'string'
              ? field.value
              : safeJsonStringify(field.value);

  return (
      <label className="label">
        {props.label}
        <textarea
            className="textarea monospace"
            rows={6}
            placeholder={`{"${t('fields.example')}": "${t('fields.value')}"}`}
            value={value}
            disabled={readOnly}
            onChange={(e) => {
              if (readOnly) return;
              const s = e.target.value;
              if (!s.trim()) {
                field.onChange(null);
                return;
              }
              try {
                field.onChange(JSON.parse(s));
              } catch {
                field.onChange(s);
              }
            }}
        />
        {props.helper ? <small className="muted">{props.helper}</small> : null}
      </label>
  );
}

export function JsonTableField<TForm extends AnyForm>(props: {
  label: string;
  name: string;
  control: Control<TForm>;
  helper?: string;
  slug?: string;
  draftKey?: string;
  projectId?: number;
  projectSlug?: string;
  previewMode?: 'table' | 'link';
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const { field } = useController({ control: props.control, name: props.name as any });
  const contextReadOnly = useProjectReadOnly();
  const readOnly = props.readOnly ?? contextReadOnly;

  if (props.previewMode === 'link' && props.slug) {
    const model = toTableModel(field.value);
    const isAdminArea = window.location.pathname.startsWith('/administracion');
    const href = isAdminArea
        ? `/administracion/proyectos/${props.projectSlug}/editor-json/${props.slug}`
        : `/proyectos/${props.projectSlug}/visor-tabla/${props.slug}`;

    return (
        <Link className="jsonFieldLinkCard" to={href}>
          <div className="jsonFieldLinkCardMain">
            <strong>{props.label}</strong>
            <span className="muted">
                        {model.rows.length} {model.rows.length === 1 ? t('fields.rows') : t('fields.rows_plural')} - {model.columns.length} {model.columns.length === 1 ? t('fields.columns') : t('fields.columns_plural')}
                    </span>
          </div>
          <span className="jsonFieldLinkCardAction">
                    {readOnly ? t('fields.viewInFullEditor') : t('fields.openInFullEditor')}
                </span>
        </Link>
    );
  }

  return (
      <JsonTableEditor
          label={props.label}
          value={field.value}
          helper={props.helper}
          onChange={field.onChange}
          readOnly={readOnly}
          title={props.label}
      />
  );
}

export function JsonTableEditor(props: {
  label: string;
  value: unknown;
  onChange?: (value: unknown) => void;
  helper?: string;
  fullPageHref?: string;
  fullHeight?: boolean;
  readOnly?: boolean;
  title?: string;
}) {
  const { t } = useTranslation();
  const [model, setModel] = React.useState<TableModel>(() => toTableModel(props.value));
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const hotRef = React.useRef<HotInstance | null>(null);
  const modelRef = React.useRef<TableModel>(model);
  const syncingRef = React.useRef(false);
  const lastEmittedJsonRef = React.useRef<string | null>(null);
  const propsRef = React.useRef(props);

  React.useEffect(() => {
    propsRef.current = props;
  });

  React.useEffect(() => {
    const stringifiedValue = JSON.stringify(props.value);
    if (stringifiedValue === lastEmittedJsonRef.current) return;
    const nextModel = toTableModel(props.value);
    modelRef.current = nextModel;
    setModel(nextModel);
  }, [props.value]);

  const commit = React.useCallback(
      (
          nextColumns: string[],
          nextRows: string[][],
          nextCellStyles?: Record<string, CellStyle>,
          nextColumnWidths?: number[]
      ) => {
        const latestProps = propsRef.current;
        if (latestProps.readOnly) return;

        const normalizedColumns = normalizeColumns(nextColumns);
        if (normalizedColumns.length === 0) normalizedColumns.push('1');
        const normalizedRows = normalizeRows(nextRows, normalizedColumns.length);

        const nextModel = {
          columns: normalizedColumns,
          rows: normalizedRows,
          cellStyles: nextCellStyles || {},
          columnWidths: nextColumnWidths || Array(normalizedColumns.length).fill(50),
        };

        modelRef.current = nextModel;
        syncingRef.current = true;
        setModel(nextModel);

        if (hotRef.current) {
          const sel = hotRef.current.getSelected();
          hotRef.current.updateSettings({
            data: normalizedRows,
            colHeaders: normalizedColumns,
            colWidths: nextModel.columnWidths,
          });
          applyCellStyles(hotRef.current, nextModel);
          if (sel && sel[0]) {
            try { hotRef.current.selectCell(sel[0][0], sel[0][1], sel[0][2], sel[0][3]); } catch (e) { }
          }
        }

        const jsonOutput = toJsonTable(normalizedColumns, normalizedRows, nextModel.cellStyles, nextModel.columnWidths);
        const stringified = JSON.stringify(jsonOutput);
        if (stringified !== lastEmittedJsonRef.current) {
          lastEmittedJsonRef.current = stringified;
          latestProps.onChange?.(jsonOutput);
        }

        window.setTimeout(() => { syncingRef.current = false; }, 10);
      },
      []
  );

  const updateStateFromHot = React.useCallback((source?: string) => {
    if (syncingRef.current || !hotRef.current) return;
    const state = extractVisualState(hotRef.current, modelRef.current.columns, modelRef.current.cellStyles || {});
    commit(state.nextColumns, state.nextRows, state.nextStyles, state.nextWidths);

    if (source === 'afterColumnMove') {
      const plugin = hotRef.current.getPlugin('manualColumnMove');
      if (plugin && typeof plugin.clearManualMoves === 'function') {
        plugin.clearManualMoves();
      }
    }
    if (source === 'afterRowMove') {
      const plugin = hotRef.current.getPlugin('manualRowMove');
      if (plugin && typeof plugin.clearManualMoves === 'function') {
        plugin.clearManualMoves();
      }
    }
  }, [commit]);

  const renameRef = React.useRef(() => { });
  renameRef.current = () => {
    if (!hotRef.current) return;
    const sel = hotRef.current.getSelectedLast();
    if (!sel) return;
    const colIdx = sel[1];
    const currentName = modelRef.current.columns[hotRef.current.toPhysicalColumn(colIdx)] || `${colIdx + 1}`;
    const nextName = window.prompt(t('fields.newColumnName'), currentName);
    if (nextName == null || !nextName.trim()) return;

    const state = extractVisualState(hotRef.current, modelRef.current.columns, modelRef.current.cellStyles || {});
    state.nextColumns[colIdx] = nextName.trim();
    commit(state.nextColumns, state.nextRows, state.nextStyles, state.nextWidths);
  };

  React.useEffect(() => {
    const Handsontable = HandsontableModule.default ?? HandsontableModule;
    if (!hostRef.current || hotRef.current) return;

    hotRef.current = new Handsontable(hostRef.current, {
      data: modelRef.current.rows,
      colHeaders: modelRef.current.columns,
      colWidths: modelRef.current.columnWidths || 100,
      rowHeaders: true,
      width: '100%',
      height: '100%',
      stretchH: 'none',
      manualColumnResize: true,
      manualColumnMove: true,
      manualRowMove: true,
      outsideClickDeselects: false,
      readOnly: propsRef.current.readOnly,
      licenseKey: 'non-commercial-and-evaluation',
      cells: function (row: number, col: number) {
        return {
          renderer: function (instance: any, td: HTMLTableCellElement, row: number, col: number, prop: string | number, value: any, cellProperties: any) {
            Handsontable.renderers.TextRenderer.apply(this, arguments as any);
            td.style.whiteSpace = 'nowrap';
            td.style.overflow = 'hidden';
            td.style.textOverflow = 'ellipsis';
            td.style.display = 'table-cell';
          }
        };
      },
      contextMenu: propsRef.current.readOnly ? false : {
        items: {
          row_above: {}, row_below: {}, col_left: {}, col_right: {}, remove_row: {}, remove_col: {},
          separator_1: '---------',
          rename_column: {
            name: t('fields.renameColumn'),
            callback: () => renameRef.current(),
          },
        }
      },
      afterChange: (changes: any, source: string) => {
        if (source !== 'loadData') updateStateFromHot();
      },
      afterCreateCol: () => updateStateFromHot(),
      afterRemoveCol: () => updateStateFromHot(),
      afterCreateRow: () => updateStateFromHot(),
      afterRemoveRow: () => updateStateFromHot(),
      afterColumnResize: () => updateStateFromHot(),
      afterColumnMove: () => updateStateFromHot('afterColumnMove'),
      afterRowMove: () => updateStateFromHot('afterRowMove')
    });

    return () => { hotRef.current?.destroy(); hotRef.current = null; };
  }, [updateStateFromHot]);

  React.useEffect(() => {
    if (!hotRef.current) return;
    const currentData = hotRef.current.getData();
    if (JSON.stringify(currentData) !== JSON.stringify(model.rows)) {
      hotRef.current.updateSettings({
        data: model.rows,
        colHeaders: model.columns,
        colWidths: model.columnWidths || 100,
        readOnly: props.readOnly,
      });
      applyCellStyles(hotRef.current, model);
    }
  }, [model, props.readOnly]);

  const addRow = () => {
    if (!hotRef.current || propsRef.current.readOnly) return;
    hotRef.current.deselectCell();
    const nextRows = [...modelRef.current.rows, Array(modelRef.current.columns.length || 1).fill('')];
    commit(modelRef.current.columns, nextRows, modelRef.current.cellStyles, modelRef.current.columnWidths);
  };

  const addColumn = () => {
    if (!hotRef.current || propsRef.current.readOnly) return;
    hotRef.current.deselectCell();
    const nextCols = [...modelRef.current.columns, ''];
    const nextWidths = [...(modelRef.current.columnWidths || Array(modelRef.current.columns.length).fill(100)), 100];
    const nextRows = modelRef.current.rows.map(r => [...r, '']);
    commit(nextCols, nextRows, modelRef.current.cellStyles, nextWidths);
  };

  const deleteRow = () => {
    if (!hotRef.current || propsRef.current.readOnly) return;
    const sel = hotRef.current.getSelectedLast();
    if (sel) {
      const start = Math.min(sel[0], sel[2]);
      const amount = Math.abs(sel[2] - sel[0]) + 1;
      const nextRows = [...modelRef.current.rows];
      nextRows.splice(start, amount);
      hotRef.current.deselectCell();
      commit(modelRef.current.columns, nextRows.length ? nextRows : [['']], modelRef.current.cellStyles, modelRef.current.columnWidths);
    }
  };

  const deleteCol = () => {
    if (!hotRef.current || propsRef.current.readOnly) return;
    const sel = hotRef.current.getSelectedLast();
    if (sel) {
      const start = Math.min(sel[1], sel[3]);
      const amount = Math.abs(sel[3] - sel[1]) + 1;
      const nextCols = [...modelRef.current.columns];
      nextCols.splice(start, amount);
      const nextWidths = [...(modelRef.current.columnWidths || Array(modelRef.current.columns.length).fill(100))];
      nextWidths.splice(start, amount);
      const nextRows = modelRef.current.rows.map(r => {
        const newRow = [...r];
        newRow.splice(start, amount);
        return newRow;
      });
      hotRef.current.deselectCell();
      commit(nextCols.length ? nextCols : ['1'], nextRows.length ? nextRows : [['']], modelRef.current.cellStyles, nextWidths);
    }
  };

  const statsLabel = `${model.rows.length} ${model.rows.length === 1 ? t('fields.rows') : t('fields.rows_plural')} - ${model.columns.length} ${model.columns.length === 1 ? t('fields.columns') : t('fields.columns_plural')}`;

  return (
      <div className={`jsonHotShell ${props.fullHeight ? 'isFullPage' : ''}`} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="jsonHotToolbar">
          <div className="jsonHotToolbarMeta">
            <strong>{props.title || props.label || t('fields.table')}</strong>
            <span className="muted">{statsLabel}</span>
          </div>
          {!props.readOnly && (
              <div className="jsonHotToolbarActions">
                <button className="btn btnSmall btnSecondary" type="button" onClick={addRow}>{t('fields.addRow')}</button>
                <button className="btn btnSmall btnSecondary" type="button" onClick={addColumn}>{t('fields.addColumn')}</button>
                <button className="btn btnSmall btnGhost" type="button" onClick={() => renameRef.current()}>{t('fields.renameColumn')}</button>
                <button className="btn btnSmall btnGhost" type="button" onClick={deleteRow}>{t('fields.deleteRow')}</button>
                <button className="btn btnSmall btnGhost" type="button" onClick={deleteCol}>{t('fields.deleteCol')}</button>
              </div>
          )}
        </div>
        <div className="jsonHotGrid" style={{ position: 'relative', flexGrow: 1, height: props.fullHeight ? 'calc(100vh - 340px)' : 500 }}>
          <div ref={hostRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }} />
        </div>
        {props.helper ? <small className="muted" style={{ padding: '8px' }}>{props.helper}</small> : null}
      </div>
  );
}


function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isStructuredTable(value: unknown): value is { columns: string[]; rows: any[]; cellStyles?: Record<string, CellStyle>; columnWidths?: number[] } {
  return isRecord(value) && Array.isArray(value.columns) && Array.isArray(value.rows);
}

export function toTableModel(value: unknown): TableModel {
  if (typeof value === 'string') {
    try {
      return toTableModel(JSON.parse(value));
    } catch {
      return { columns: ['1'], rows: [['']], cellStyles: {}, columnWidths: [100] };
    }
  }

  if (isRecord(value) && Array.isArray(value.rows)) {
    let rawColumns = Array.isArray(value.columns) ? [...value.columns] : [];

    let maxRowLength = rawColumns.length;

    const rows = value.rows.map((row) => {
      if (Array.isArray(row)) {
        maxRowLength = Math.max(maxRowLength, row.length);
        return row.map(stringifyValue);
      }
      if (isRecord(row)) {
        if (rawColumns.length === 0) {
          rawColumns = Object.keys(row);
        }
        maxRowLength = Math.max(maxRowLength, rawColumns.length);
        return rawColumns.map((col) => stringifyValue(row[col]));
      }
      return [];
    });

    while (rawColumns.length < maxRowLength) {
      rawColumns.push('');
    }

    if (rawColumns.length === 0) {
      rawColumns.push('1');
    }

    const columns = normalizeColumns(rawColumns);
    const normalizedRows = normalizeRows(rows, columns.length);

    return {
      columns,
      rows: normalizedRows.length > 0 ? normalizedRows : [['']],
      cellStyles: isRecord(value.cellStyles) ? (value.cellStyles as Record<string, CellStyle>) : {},

      columnWidths: Array.isArray(value.columnWidths) && value.columnWidths.length === columns.length
          ? (value.columnWidths as number[])
          : Array(columns.length).fill(100),
    };
  }

  return { columns: ['1'], rows: [['']], cellStyles: {}, columnWidths: [100] };
}

function toJsonTable(columns: string[], rows: string[][], cellStyles: Record<string, CellStyle> = {}, columnWidths?: number[]) {
  return {
    columns: normalizeColumns(columns),
    rows: normalizeRows(rows, columns.length),
    cellStyles,
    columnWidths,
  };
}

function normalizeColumns(columns: any[]) {
  const used = new Set<string>();
  let nextNum = 1;

  return columns.map((col) => {
    let base = String(col ?? "").trim();
    const isInvalid = !base || base === "undefined" || base === "null" || base === "NaN" || base === "[object Object]";

    if (isInvalid || /^\d+$/.test(base)) {
      while (used.has(String(nextNum))) {
        nextNum++;
      }
      base = String(nextNum);
      nextNum++;
    }

    let candidate = base;
    let counter = 2;
    while (used.has(candidate)) {
      candidate = `${base}_${counter++}`;
    }
    used.add(candidate);
    return candidate;
  });
}

function normalizeRows(rows: string[][], columnCount: number) {
  if (!rows || rows.length === 0) return [Array(columnCount).fill('')];
  return rows.map((row) => Array.from({ length: columnCount }, (_, i) => String(Array.isArray(row) ? (row[i] ?? '') : '')));
}
function extractVisualState(hot: HotInstance, currentColumns: string[], currentStyles: Record<string, CellStyle>) {
  const rowsCount = hot.countRows();
  const colsCount = hot.countCols();

  const nextColumns: string[] = [];
  const nextWidths: number[] = [];
  const nextRows: string[][] = [];
  const nextStyles: Record<string, CellStyle> = {};

  const seenPCols = new Set<number>();
  const v2pCol = new Map<number, number>();

  for (let c = 0; c < colsCount; c++) {
    let pCol = hot.toPhysicalColumn(c);
    if (seenPCols.has(pCol)) {
      pCol = -1;
    } else {
      seenPCols.add(pCol);
    }
    v2pCol.set(c, pCol);

    let colName = pCol !== -1 ? currentColumns[pCol] : undefined;
    if (!colName || colName === "undefined" || colName === "null") {
      colName = "";
    }
    nextColumns.push(colName);
    nextWidths.push(hot.getColWidth(c) || 100);
  }

  const seenPRows = new Set<number>();
  for (let r = 0; r < rowsCount; r++) {
    let pRow = hot.toPhysicalRow(r);
    if (seenPRows.has(pRow)) {
      pRow = -1;
    } else {
      seenPRows.add(pRow);
    }

    const rowData: string[] = [];
    for (let c = 0; c < colsCount; c++) {
      const val = hot.getDataAtCell(r, c);
      rowData.push(val == null ? '' : String(val));

      const pCol = v2pCol.get(c)!;
      if (pRow !== -1 && pCol !== -1) {
        const oldKey = `${pRow}:${pCol}`;
        const newKey = `${r}:${c}`;
        if (currentStyles && currentStyles[oldKey]) {
          nextStyles[newKey] = currentStyles[oldKey];
        }
      }
    }
    nextRows.push(rowData);
  }

  return { nextColumns, nextRows, nextStyles, nextWidths };
}

function stringifyValue(v: unknown) { return v == null ? '' : String(v); }
function safeJsonStringify(v: unknown) { try { return JSON.stringify(v, null, 2); } catch { return String(v); } }

function applyCellStyles(hot: HotInstance, model: TableModel) {
  const totalRows = model.rows.length;
  const totalCols = model.columns.length;
  for (let row = 0; row < totalRows; row++) {
    for (let col = 0; col < totalCols; col++) {
      const key = `${row}:${col}`;
      hot.setCellMeta(row, col, 'className', model.cellStyles?.[key]?.bold ? 'htBold' : '');
    }
  }
  hot.render();
}

export function CurrencyField(props: { label: string; name: string; register?: any }) {
  const readOnly = useProjectReadOnly();
  const { control, setValue } = useFormContext();
  const value = useWatch({ control, name: props.name });
  const [isFocused, setIsFocused] = React.useState(false);

  const format = (val: any) => {
    const num = Number(val);
    if (isNaN(num) || val == null) return '';
    return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num) + ' €';
  };

  if (readOnly) {
    return (
        <div className="label">
          {props.label}
          <div className="input" style={{ backgroundColor: '#fcfdfe', display: 'flex', alignItems: 'center', minHeight: '52px', opacity: 1 }}>
            {format(value)}
          </div>
        </div>
    );
  }

  const numericValue = value === '' || value === null || value === undefined ? '' : Number(value);
  const displayValue = isFocused ? numericValue : format(value).replace(' €', '');

  return (
      <label className="label">
        {props.label}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
              type={isFocused ? "number" : "text"}
              step="0.01"
              className="input"
              value={isFocused ? (displayValue ?? '') : `${displayValue ?? 0}€`}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={(e) => {
                const val = e.target.value;
                setValue(props.name, val === '' ? null : Number(val), { shouldDirty: true });
              }}
          />
        </div>
      </label>
  );
}
